import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { closeDialog } from "../../../redux/reducers/DialogGeneral.js";
import RadioButtonCustom from "../../UI/RadiobuttonCustom.js/RadioButtonCustom.js";
import GeneralCustomDateTimePicker from "../../UI/TimePicker/GeneralCustomDateTimePicker.js";
import DayRipetizioniBox from "../../Dashboard/Stepper/stepperComponent/DayRipetizioniBox/DayRipetizioniBox.js";
import moment from "moment";
import { ControlloRipetizioneStatus, returnRipetizioneGiorni  } from "../../../redux/reducers/prenotazioneStepper-reducer.js";
import { AutocompleteCustom } from "../../UI/AutoComplete/AutoComplete.js";
import { getUsersFiltered, deletePrenotazioniMassiva } from "../../../const/urlAction.js";
import handlerError from "../../../functions/handlerError.js";
import { notificationOpen } from "../../../redux/reducers/notification-reducer.js";
import axios from "axios";
import handlerConfiguration from "../../../functions/general/handlerConfiguration.js";
import { radioButtonDeleteOptions, radioButtonGuestsOptions } from "../../../const/RadioButton/RadioButtonListRadio.js";
import { setRiepilogoDelete } from "../../../redux/reducers/riepilogoDelete-reducer.js";
import { showLoader, hideLoader } from "../../../redux/reducers/appLoader-reducer";
import { loadprenotazioniTableState } from "../../../redux/reducers/prenotazioneTable-reducer";
import { DeleteOption, GuestOption } from "../../../const/enums/deleteReservationEnums";


const DialogDeleteReservation = ({ isOpen }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const [usersOptions, setUsersOptions] = useState([]);
  const configurations = useSelector((state) => state.configurations);
  const ripetizioneGiorni = useSelector(
    (state) => state.prenotazioneStepper.ripetizioneGiorni
  );
  const utenteLoggato = useSelector((state) => state.userDate);
  const { maxGiorniRipetizione } = handlerConfiguration(configurations);
  const now = new Date();
  const maxday = new Date(
    new Date(Date.now()).setDate(now.getDate() + maxGiorniRipetizione)
  );

  const validationSchema = Yup.object({
    fromDate: Yup.date()
      .nullable()
      .required(t("validation_required")),
    toDate: Yup.date()
      .nullable()
      .required(t("validation_required"))
      .min(Yup.ref("fromDate"), t("validation_enddate_after_startdate")),
    daysOfWeek: Yup.array()
      .min(1, t("validation_required")),
    selectedUsers: Yup.array().when("deleteRadioValue", {
      is: DeleteOption.Other,
      then: (schema) => schema.min(1, t("validation_required")),
      otherwise: (schema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues: {
      deleteRadioValue: DeleteOption.Me,
      guestsRadioValue: GuestOption.Users,
      fromDate: null,
      toDate: null,
      daysOfWeek: [],
      selectedUsers: [],
      notes: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { resetForm }) => {
      dispatch(showLoader());

      const usersId =
        values.deleteRadioValue === DeleteOption.Me
          ? [utenteLoggato.id]
          : values.selectedUsers.map((u) => u.idUtente);

      const payload = {
        usersId,
        deleteOption: Number(values.deleteRadioValue),
        guestOption: Number(values.guestsRadioValue),
        fromDate: moment(values.fromDate).format("YYYY-MM-DD"),
        toDate: moment(values.toDate).format("YYYY-MM-DD"),
        daysOfWeek: ripetizioneGiorni,
        notes: values.notes,
      };
      
      try {
        const { action, method } = deletePrenotazioniMassiva;
        const response = await axios({
          method,
          url: process.env.REACT_APP_URL + action,
          data: payload,
        });

        dispatch(setRiepilogoDelete(response.data.result));
        dispatch(
          notificationOpen({ message: response.data.message, status: "success" })
        );

        dispatch(loadprenotazioniTableState());
        resetForm();
        dispatch(ControlloRipetizioneStatus(false));
      } catch (error) {
        dispatch(notificationOpen(handlerError(error)));
      }

      dispatch(hideLoader());
      dispatch(closeDialog());
    },
  });

  const isConfirmDisabled =
    !formik.isValid ||
    !formik.values.fromDate ||
    !formik.values.toDate ||
    formik.values.daysOfWeek.length === 0 ||
    (formik.values.deleteRadioValue === DeleteOption.Other &&
     formik.values.selectedUsers.length === 0);

  const handleCancel = () => {
    formik.resetForm();
    resetDays();
    dispatch(closeDialog());
  };

  const resetDays = () => {
    dispatch(returnRipetizioneGiorni([]));
    dispatch(ControlloRipetizioneStatus(false));
  };

  useEffect(() => {
    formik.setFieldValue("daysOfWeek", ripetizioneGiorni);
  }, [ripetizioneGiorni]);

  useEffect(() => {
    const fetchUsers = async () => {
      const { action, method } = getUsersFiltered;
      try {
        const response = await axios({
          method,
          url: process.env.REACT_APP_URL + action,
        });
        setUsersOptions(response.data.result);
      } catch (error) {
        dispatch(notificationOpen(handlerError(error)));
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    dispatch(ControlloRipetizioneStatus(false));
  }, []);

  useEffect(() => {
    dispatch(returnRipetizioneGiorni([]));
    dispatch(ControlloRipetizioneStatus(true));

    if (!isOpen) {
        formik.resetForm();
        resetDays();
    }
  }, [isOpen]);


  return (
    <Dialog
      open={isOpen}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="delete-dialog-title"
    >
      <DialogTitle id="delete-dialog-title">
        {t("delete_a_reservation")}
      </DialogTitle>

      <DialogContent>
        {/* Opzioni cancellazione */}
        <Grid item xs={12}>
          <RadioButtonCustom
            GroupName={t("delete_v3")}
            startValueReset={formik.values.deleteRadioValue}
            labelList={radioButtonDeleteOptions}
            handleChange={(value) =>
              formik.setFieldValue("deleteRadioValue", value)
            }
            tabNumber={radioButtonDeleteOptions}
          />
        </Grid>

        {/* Opzioni ospiti */}
        <Grid item xs={12}>
          <RadioButtonCustom
            GroupName={t("guests_options")}
            startValueReset={formik.values.guestsRadioValue}
            labelList={radioButtonGuestsOptions}
            handleChange={(value) =>
              formik.setFieldValue("guestsRadioValue", value)
            }
            tabNumber={radioButtonGuestsOptions}
          />
        </Grid>

        {/* Autocomplete utenti */}
        {formik.values.deleteRadioValue === DeleteOption.Other && (
          <Grid item xs={12} mb={1}>
            <AutocompleteCustom
              label={t("Delete_behalf_of")}
              field={["nominativo"]}
              checkField={"nominativo"}
              fullWidth
              keyId={"nominativo"}
              openOnFocus
              optionsAutoComplete={usersOptions}
              handleValueReturned={(value) =>
                formik.setFieldValue("selectedUsers", value)
              }
              startValueAutoComplete={formik.values.selectedUsers}
              multiple
              error={
                formik.touched.selectedUsers &&
                Boolean(formik.errors.selectedUsers)
              }
              helperText={
                formik.touched.selectedUsers && formik.errors.selectedUsers
              }
            />
          </Grid>
        )}

        <Grid container spacing={2}>
          {/* Data inizio */}
          <Grid item xs={12} sm={6}>
            <GeneralCustomDateTimePicker
              TypeTimePicker={5}
              label={t("Start_Date")}
              valueStart={formik.values.fromDate}
              handleChange={(value) => {
                formik.setFieldValue("fromDate", value);
                formik.setFieldTouched("fromDate", true);
              }}
              minDate={now}
              maxDate={maxday}
              placeholder={t("Booking_date_placeholder")}
              error={
                formik.touched.fromDate && Boolean(formik.errors.fromDate)
              }
              helperText={formik.touched.fromDate && formik.errors.fromDate}
            />
          </Grid>

          {/* Data fine */}
          <Grid item xs={12} sm={6}>
            <GeneralCustomDateTimePicker
              TypeTimePicker={5}
              label={t("End_Date")}
              valueStart={formik.values.toDate}
              handleChange={(value) => {
                formik.setFieldValue("toDate", value);
                formik.setFieldTouched("toDate", true);
              }}
              minDate={formik.values.fromDate || now}
              maxDate={maxday}
              placeholder={t("Booking_date_placeholder")}
              error={formik.touched.toDate && Boolean(formik.errors.toDate)}
              helperText={formik.touched.toDate && formik.errors.toDate}
            />
          </Grid>

          {/* Giorni ripetizione */}
          <Grid item xs={12} mt={1}>
            <DayRipetizioniBox />
            {formik.touched.daysOfWeek && formik.errors.daysOfWeek && (
              <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                {formik.errors.daysOfWeek}
              </Typography>
            )}
          </Grid>

          {/* Note */}
          <Grid item xs={12}>
            <TextField
              label={t("annotation")}
              fullWidth
              multiline
              minRows={2}
              value={formik.values.notes}
              onChange={(e) => formik.setFieldValue("notes", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          {t("dialogButtonGeneral_Cancel")}
        </Button>
        <Button
          onClick={formik.handleSubmit}
          color="primary"
          variant="contained"
          disabled={isConfirmDisabled || formik.isSubmitting}
        >
          {t("delete_v3")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogDeleteReservation;